import { Injectable, NgZone, AfterViewInit } from '@angular/core';

declare var $: any;
declare var particlesJS: any;
declare var skrollr: any;
declare var Atropos: any;
declare var Swiper: any;
declare var anime: any;
declare var imagesLoaded: any;

@Injectable({
  providedIn: 'root'
})
export class MainInitService {

  private skrollerInstance: any;
  private swiperInstances: any[] = [];

  constructor(private ngZone: NgZone) { }

  // Main initialization function - mimics main.js initialization
  init(): void {
    this.ngZone.runOutsideAngular(() => {
      // Wait for jQuery and all libraries to load
      setTimeout(() => {
        if (typeof $ === 'undefined') {
          console.warn('jQuery not loaded, retrying...');
          setTimeout(() => this.init(), 500);
          return;
        }

        // Initialize header menu toggle
        this.initHeaderMenu();
        
        // Initialize touch device detection
        this.initTouchDevice();
        
        // Initialize jQuery appear for scroll-triggered animations
        this.initJQueryAppear();
        
        // Initialize smooth scroll navigation
        this.initSmoothScroll();
        
        // Initialize counters
        this.initCounters();
        
        // Initialize swipers
        this.initSwipers();
        
        // Initialize particles
        this.initParticles();
        
        // Initialize skrollr
        this.initSkrollr();
        
        // Initialize atropos
        this.initAtropos();
        
        // Initialize parallax
        this.initParallax();
        
        // Initialize custom cursor
        this.initCustomCursor();
        
        // Initialize hover effects
        this.initHoverEffects();
        
        // Initialize scroll progress
        this.initScrollProgress();
        
        // Initialize lightbox/magnific popup
        this.initLightbox();
        
        // Initialize isotope for portfolio
        this.initIsotope();
        
        // Initialize overlap sections
        this.initOverlapSections();
        
        // Initialize progress bars
        this.initProgressBars();
        
        // Initialize scroll animations
        this.initScrollAnimations();
      }, 500);
    });
  }

  private initHeaderMenu(): void {
    // Header push menu open
    $(document).on('click', '.header-push-button .push-button', () => {
      $('html, body').toggleClass('show-menu');
    });

    // Header push menu close
    $(document).on('click', '.close-menu', () => {
      $('html, body').removeClass('show-menu');
      $('.sub-menu.show').collapse('hide');
    });

    // Close on escape key
    $(document).on('keydown', (e: any) => {
      if (e.keyCode === 27) {
        $('html, body').removeClass('show-menu');
        $('.navbar-collapse.collapse').collapse('hide');
      }
    });
  }

  private initTouchDevice(): void {
    const nav = navigator as any;
    if (('ontouchstart' in window) || (navigator.maxTouchPoints > 0) || (nav.msMaxTouchPoints > 0)) {
      document.body.classList.add('is-touchable');
    }
  }

  private initJQueryAppear(): void {
    if (typeof $.fn.appear === 'function') {
      $('.vertical-counter, .counter, .progress-bar, .pie-chart-style-01, .attractive-hover, .splitting-animation, .section-dark, footer, [data-anime], [data-fancy-text]').each((index: number, element: Element) => {
        $(element).appear().trigger('resize');
      });
    }
  }

  private initSmoothScroll(): void {
    if (typeof $.fn.smoothScroll === 'function') {
      $('.inner-link').smoothScroll({
        speed: 800,
        offset: 0,
        beforeScroll: () => {
          $('.navbar-collapse.collapse').collapse('hide');
          if ($('body').hasClass('show-menu')) {
            $('html, body').removeClass('show-menu');
          }
        }
      });
    } else {
      // Fallback smooth scroll
      $('.inner-link').on('click', (e: any) => {
        e.preventDefault();
        const href = $(e.currentTarget).attr('href');
        if (href && href.startsWith('#')) {
          const target = $(href);
          if (target.length) {
            $('html, body').animate({
              scrollTop: target.offset()!.top
            }, 800);
            if ($('body').hasClass('show-menu')) {
              $('html, body').removeClass('show-menu');
            }
          }
        }
      });
    }
  }

  private initCounters(): void {
    // Vertical counter initialization
    $('.vertical-counter').each((index: number, element: Element) => {
      const $this = $(element);
      const counterValue = $this.attr('data-to');
      if (!counterValue) return;
      
      const individualValue = counterValue.toString().split('');
      const valueLength = counterValue.length;

      // Add vertical counter numbers
      for (let i = 0; i < valueLength; i++) {
        $this.append('<span class="vertical-counter-number"><ul><li>0</li><li>1</li><li>2</li><li>3</li><li>4</li><li>5</li><li>6</li><li>7</li><li>8</li><li>9</li></ul></span>');
      }

      $this.find('.vertical-counter-number').each((idx: number, el: Element) => {
        $(el).attr('data-to', individualValue[idx]);
      });
    });

    // Counter animation on appear
    if (typeof $.fn.appear === 'function') {
      $(document).on('appear', '.vertical-counter', (e: any) => {
        const $this = $(e.target);
        if (!$this.hasClass('appear')) {
          $this.addClass('appear');
          $this.find('.vertical-counter-number').each((index: number, element: Element) => {
            const $el = $(element);
            const value = parseInt($el.attr('data-to') || '0');
            if (value <= 9 && typeof anime !== 'undefined') {
              anime({
                targets: element.querySelector('ul'),
                translateY: [0, `${-value * 10}%`],
                duration: 2000,
                easing: 'easeOutCubic'
              });
            }
          });
        }
      });
    }

    // Regular counter
    if (typeof $.fn.countTo === 'function') {
      $(document).on('appear', '.counter', (e: any) => {
        const $this = $(e.target);
        if (!$this.hasClass('appear')) {
          const options = $this.data('countToOptions') || {};
          $this.countTo(options);
          $this.addClass('appear');
        }
      });
    }
  }

  private initSwipers(): void {
    if (typeof Swiper === 'undefined') {
      setTimeout(() => this.initSwipers(), 200);
      return;
    }

    // Initialize swipers with data-slider-options
    $('[data-slider-options]').each((index: number, element: Element) => {
      const $element = $(element);
      const sliderOptions = $element.attr('data-slider-options');
      
      if (sliderOptions) {
        try {
          const options = JSON.parse(sliderOptions);
          const swiper = new Swiper(element, options);
          this.swiperInstances.push(swiper);
        } catch (e) {
          console.error('Error parsing swiper options:', e);
        }
      }
    });
  }

  private initParticles(): void {
    const particleItems = $('[data-particle="true"]');
    if (particleItems.length && typeof particlesJS === 'function') {
      particleItems.each((index: number, element: Element) => {
        const $element = $(element);
        const particleId = $element.attr('id');
        const particleOptionsAttr = $element.attr('data-particle-options');
        
        if (particleId && particleOptionsAttr) {
          try {
            // Create canvas if needed
            if (!$element.find('.particles-js-canvas-el').length) {
              const canvas = document.createElement('canvas');
              canvas.className = 'particles-js-canvas-el';
              element.appendChild(canvas);
            }
            
            const particleOptions = JSON.parse(particleOptionsAttr);
            particlesJS(particleId, particleOptions);
          } catch (e) {
            console.error('Error initializing particles:', e);
          }
        }
      });
    }
  }

  private initSkrollr(): void {
    if (typeof skrollr !== 'undefined' && window.innerWidth >= 1200) {
      this.skrollerInstance = skrollr.init({
        forceHeight: false,
        smoothScrollingDuration: 1000,
        mobileCheck: () => {
          return false;
        }
      });
    }
  }

  private initAtropos(): void {
    if (typeof Atropos !== 'undefined' && window.innerWidth > 1199) {
      $('[data-atropos]').each((index: number, element: Element) => {
        try {
          new Atropos({
            el: element
          });
        } catch (e) {
          console.error('Error initializing Atropos:', e);
        }
      });
    }
  }

  private initParallax(): void {
    if (typeof $.fn.parallax !== 'undefined') {
      $('[data-parallax-background-ratio]').each((index: number, element: Element) => {
        const $el = $(element);
        const ratio = $el.attr('data-parallax-background-ratio') || 0.5;
        $el.parallax('50%', parseFloat(ratio));
      });
    }
  }

  private initCustomCursor(): void {
    if ($('body').hasClass('custom-cursor')) {
      const cursorInner = document.querySelector('.circle-cursor-inner');
      const cursorOuter = document.querySelector('.circle-cursor-outer');
      
      if (!cursorInner || !cursorOuter) {
        return;
      }

      let magneticFlag = false;

      $(document).on('mousemove', (e: any) => {
        if (!magneticFlag) {
          (cursorOuter as HTMLElement).style.transform = `translate(${e.clientX}px, ${e.clientY}px)`;
        }
        (cursorInner as HTMLElement).style.opacity = '1';
        (cursorInner as HTMLElement).style.transform = `translate(${e.clientX}px, ${e.clientY}px)`;
      });

      $('a').on('mouseenter', () => {
        cursorInner.classList.add('cursor-link-hover');
        cursorOuter.classList.add('cursor-link-hover');
      }).on('mouseleave', () => {
        cursorInner.classList.remove('cursor-link-hover');
        cursorOuter.classList.remove('cursor-link-hover');
      });
    }
  }

  private initHoverEffects(): void {
    // Portfolio mousetip
    $(document).on('mousemove', '.mousetip-wrapper', (e: any) => {
      const $this = $(e.currentTarget);
      const mouseX = e.pageX - $this.offset()!.left + 20;
      const mouseY = e.pageY - $this.offset()!.top + 20;
      $this.find('.caption').show().css({
        top: mouseY,
        left: mouseX
      });
    });
  }

  private initScrollProgress(): void {
    $(window).on('scroll', () => {
      const scrollTop = document.documentElement.scrollTop;
      if (scrollTop > 200) {
        $('.scroll-progress').addClass('visible');
      } else {
        $('.scroll-progress').removeClass('visible');
      }

      const scrollHeight = document.documentElement.scrollHeight;
      const windowHeight = document.documentElement.clientHeight;
      const maxScrollTop = scrollHeight - windowHeight;
      const scrollPercentage = (scrollTop / (maxScrollTop - 200)) * 100;
      $('.scroll-point').css('height', Math.min(scrollPercentage, 100) + '%');
    });

    // Scroll to top
    $(document).on('click', '.scroll-top', () => {
      $('html, body').animate({scrollTop: 0}, 800);
      return false;
    });
  }

  private initLightbox(): void {
    if (typeof $.fn.magnificPopup === 'function') {
      // Lightbox gallery
      $('[data-group="lightbox-gallery"]').magnificPopup({
        delegate: 'a',
        type: 'image',
        gallery: {
          enabled: true
        }
      });
    }
  }

  private initIsotope(): void {
    if (typeof $.fn.isotope === 'function' && typeof imagesLoaded === 'function') {
      $('.portfolio-wrapper').each((index: number, element: Element) => {
        const $element = $(element);
        $element.imagesLoaded(() => {
          $element.removeClass('grid-loading');
          $element.isotope({
            layoutMode: 'masonry',
            itemSelector: '.grid-item',
            percentPosition: true,
            stagger: 0,
            masonry: {
              columnWidth: '.grid-sizer'
            }
          });
        });
      });
    }
  }

  private initOverlapSections(): void {
    // This would need the actual implementation from main.js
    // For now, we'll handle it in the component CSS
  }

  private initProgressBars(): void {
    if (typeof $.fn.appear === 'function') {
      $(document).on('appear', '.progress-bar', (e: any) => {
        const $this = $(e.target);
        if (!$this.hasClass('appear')) {
          $this.addClass('appear');
          const value = parseInt($this.attr('aria-valuenow') || '0');
          if (value > 0 && typeof anime !== 'undefined') {
            anime({
              targets: e.target,
              width: ['0%', `${value}%`],
              duration: 2000,
              easing: 'easeOutExpo'
            });
          }
        }
      });
    }
  }

  private initScrollAnimations(): void {
    // Initialize data-anime animations with jQuery appear
    if (typeof $.fn.appear === 'function' && typeof anime !== 'undefined') {
      $('[data-anime]:not(.swiper [data-anime])').each((index: number, element: Element) => {
        const $self = $(element);
        const animeOptions = $self.data('anime');

        if (animeOptions && window.innerWidth > 1199) {
          $self.on('appear', () => {
            if ($self.hasClass('appear') || $self.hasClass('animating')) {
              return;
            }
            $self.addClass('animating');

            if (!$self.hasClass('appear')) {
              setTimeout(() => {
                $self.removeClass('animating').addClass('appear');
              }, animeOptions.delay || 0);

              // Animate element
              if (animeOptions.el === 'childs') {
                const children = Array.from(element.children);
                children.forEach((child: Element, idx: number) => {
                  const delay = (animeOptions.delay || 0) + (idx * (animeOptions.staggervalue || 0));
                  anime({
                    targets: child,
                    translateY: animeOptions.translateY || [30, 0],
                    opacity: animeOptions.opacity || [0, 1],
                    duration: animeOptions.duration || 600,
                    delay: delay,
                    easing: animeOptions.easing || 'easeOutQuad'
                  });
                });
              } else {
                anime({
                  targets: element,
                  ...animeOptions
                });
              }
            }
          });
        }
      });
    }
  }

  destroy(): void {
    if (this.skrollerInstance) {
      this.skrollerInstance.destroy();
    }
    this.swiperInstances.forEach(swiper => {
      if (swiper && swiper.destroy) {
        swiper.destroy();
      }
    });
    this.swiperInstances = [];
  }
}

